import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { toCsvBlob } from '@/utils/data';
import { download, downloadJsonDataAsExcelFile } from '@/utils/download';
import { CanAccessPartnerData } from '../../../providers/ViewerContextProvider';
import { Communicator } from '../../../tools/Communication';
import { Container, InventoryCheck } from '../../InventoryCheckView';

export type ContainerType = { id: number; name: string };

export type ConsumerOriginMap = Record<string, boolean>;

export async function fetchConsumerOriginMap(
  inventoryCheckId: string,
): Promise<ConsumerOriginMap> {
  const res = await Communicator.Get(
    'api/3/InventoryCheck/GetMissingContainersPreviousOwner',
    { id: inventoryCheckId },
  );
  const data = await res.json();
  const map: ConsumerOriginMap = {};
  for (const entry of data) {
    if (entry && entry.containerId) {
      map[String(entry.containerId)] = Boolean(entry.isCustomer);
    }
  }
  return map;
}

export async function fetchConsumerOriginMapsForChecks(
  inventoryChecks: InventoryCheck[],
): Promise<Record<string, ConsumerOriginMap>> {
  const ids = inventoryChecks.map((ic) => ic.id);
  const res = await Communicator.PostData(
    'api/3/InventoryCheck/GetMissingContainersPreviousOwnerBulk',
    ids,
  );
  const data = await res.json();
  const result: Record<string, ConsumerOriginMap> = {};
  for (const [inventoryId, entries] of Object.entries<any>(data)) {
    const map: ConsumerOriginMap = {};
    for (const entry of entries) {
      if (entry && entry.containerId) {
        map[String(entry.containerId)] = Boolean(entry.isCustomer);
      }
    }
    result[inventoryId] = map;
  }
  return result;
}

function yesNo(val: boolean | null | undefined): string {
  if (val == null) return '-';
  return val ? i18n.t('Common:yes') : i18n.t('Common:no');
}

function renderDateString(date: string | undefined | null, withTime = true) {
  if (!date) return '-';
  return new Date(date).toLocaleDateString(
    i18n.language,
    getDateFormatOptions(withTime, i18n.language),
  );
}

function getMissingContainerStatus(
  container: Container,
  expirationDate: string,
) {
  if (container.seenNext != null && container.seenNext > expirationDate) {
    return i18n.t('InventoryCheck:recovered');
  }
  if (container.seenNext != null) {
    return i18n.t('InventoryCheck:confirmed');
  }
  return i18n.t('InventoryCheck:stillLost');
}

export function buildMissingContainersExportRows(
  viewer: any,
  inventoryCheck: InventoryCheck,
  containerTypes: ContainerType[],
  filteredMissingContainers: Container[],
  consumerOriginMap: ConsumerOriginMap,
) {
  const showPartnerData = CanAccessPartnerData(viewer);
  const rows = filteredMissingContainers.map((c) => {
    const obj: any = {
      [i18n.t('InventoryCheck:containerState')]: getMissingContainerStatus(
        c,
        inventoryCheck.expirationDate,
      ),
      [i18n.t('Common:id')]: inventoryCheck.id,
      [i18n.t('InventoryCheck:createdAt')]: inventoryCheck.createdAt,
      [i18n.t('InventoryCheck:typeId')]: c.typeId,
      [i18n.t('InventoryCheck:typeName')]: (
        containerTypes.find((ct) => ct.id === c.typeId) || { name: '' }
      ).name,
      [i18n.t('InventoryCheck:containerName')]: c.name,
      [i18n.t('InventoryCheck:lastSeenAtStoreBeforeExpiration')]:
        renderDateString(c.lastSeenAtStoreBeforeExpiration),
      [i18n.t('InventoryCheck:transactionType')]: c.transactionType,
      [i18n.t('InventoryCheck:part')]: i18n.t('InventoryCheck:Parts:0'),
      [i18n.t('InventoryCheck:status')]: i18n.t(
        `InventoryCheck:Status:${inventoryCheck.status}`,
      ),
      [i18n.t('InventoryCheck:amount')]: '-',
      [i18n.t('InventoryCheck:fromConsumer')]: yesNo(consumerOriginMap[c.id]),
      [i18n.t('InventoryCheck:billable')]: yesNo(c.isBillable),
      [i18n.t('InventoryCheck:nonBillableReason')]: c.isBillable
        ? '-'
        : i18n.t(
            `InventoryCheck:NonBillableReason:${c.nonBillableReason ?? 'None'}`,
          ),
    };

    if (showPartnerData) {
      obj[i18n.t('InventoryCheck:containerId')] = c.id;
      obj[i18n.t('InventoryCheck:seenNext')] = renderDateString(c.seenNext);
    }
    return obj;
  });
  return rows;
}

function buildRowsForContainers(
  viewer: any,
  inventoryCheck: InventoryCheck,
  containerTypes: ContainerType[],
  containers: Container[],
  containerState: string,
) {
  const showPartnerData = CanAccessPartnerData(viewer);
  return containers.map((c) => {
    const obj: any = {
      [i18n.t('InventoryCheck:containerState')]: containerState,
      [i18n.t('Common:id')]: inventoryCheck.id,
      [i18n.t('InventoryCheck:createdAt')]: inventoryCheck.createdAt,
      [i18n.t('InventoryCheck:typeId')]: c.typeId,
      [i18n.t('InventoryCheck:typeName')]: (
        containerTypes.find((ct) => ct.id === c.typeId) || { name: '' }
      ).name,
      [i18n.t('InventoryCheck:containerName')]: c.name,
      [i18n.t('InventoryCheck:lastSeenAtStoreBeforeExpiration')]: '-',
      [i18n.t('InventoryCheck:transactionType')]: '-',
      [i18n.t('InventoryCheck:part')]: i18n.t('InventoryCheck:Parts:0'),
      [i18n.t('InventoryCheck:status')]: i18n.t(
        `InventoryCheck:Status:${inventoryCheck.status}`,
      ),
      [i18n.t('InventoryCheck:amount')]: '-',
      [i18n.t('InventoryCheck:fromConsumer')]: '-',
    };
    if (showPartnerData) {
      obj[i18n.t('InventoryCheck:containerId')] = c.id;
      obj[i18n.t('InventoryCheck:seenNext')] = '-';
    }
    return obj;
  });
}

export function buildFullInventoryExportRows(
  viewer: any,
  inventoryChecks: InventoryCheck[],
  containerTypes: ContainerType[],
  consumerMapsByInventoryId: Record<string, ConsumerOriginMap>,
) {
  const showPartnerData = CanAccessPartnerData(viewer);
  let rows: any[] = [];

  for (const inventoryCheck of inventoryChecks) {
    // Missing parts (aggregated by type and part)
    for (const missingContainerPart of inventoryCheck.missingContainerParts) {
      const existing = rows.find(
        (r) =>
          r[i18n.t('InventoryCheck:part')] ===
            i18n.t(`InventoryCheck:Parts:${missingContainerPart.part}`) &&
          r[i18n.t('InventoryCheck:typeId')] === missingContainerPart.typeId &&
          r[i18n.t('InventoryCheck:containerState')] ===
            i18n.t('InventoryCheck:lost') &&
          r[i18n.t('InventoryCheck:inventoryCheckId')] === inventoryCheck.id,
      );
      if (existing != null) {
        existing[i18n.t('InventoryCheck:amount')]++;
      } else {
        const obj: any = {
          [i18n.t('InventoryCheck:containerState')]: i18n.t(
            'InventoryCheck:lost',
          ),
          [i18n.t('InventoryCheck:inventoryCheckId')]: inventoryCheck.id,
          [i18n.t('InventoryCheck:createdAt')]: inventoryCheck.createdAt,
          [i18n.t('InventoryCheck:typeId')]: missingContainerPart.typeId,
          [i18n.t('InventoryCheck:typeName')]: (
            containerTypes.find(
              (ct) => ct.id === missingContainerPart.typeId,
            ) || { name: '' }
          ).name,
          [i18n.t('InventoryCheck:containerName')]: '-',
          [i18n.t('InventoryCheck:lastSeenAtStoreBeforeExpiration')]: '-',
          [i18n.t('InventoryCheck:transactionType')]: '-',
          [i18n.t('InventoryCheck:part')]: i18n.t(
            `InventoryCheck:Parts:${missingContainerPart.part}`,
          ),
          [i18n.t('InventoryCheck:status')]: i18n.t(
            `InventoryCheck:Status:${inventoryCheck.status}`,
          ),
          [i18n.t('InventoryCheck:amount')]: 1,
          [i18n.t('InventoryCheck:fromConsumer')]: '-',
        };
        if (showPartnerData) {
          obj[i18n.t('InventoryCheck:containerId')] = '-';
          obj[i18n.t('InventoryCheck:seenNext')] = '-';
        }
        rows.push(obj);
      }
    }

    // Missing containers (all — billable and non-billable)
    rows = rows.concat(
      inventoryCheck.missingContainers.map((c) => {
        const obj: any = {
          [i18n.t('InventoryCheck:containerState')]: i18n.t(
            'InventoryCheck:lost',
          ),
          [i18n.t('Common:id')]: inventoryCheck.id,
          [i18n.t('InventoryCheck:createdAt')]: inventoryCheck.createdAt,
          [i18n.t('InventoryCheck:typeId')]: c.typeId,
          [i18n.t('InventoryCheck:typeName')]: (
            containerTypes.find((ct) => ct.id === c.typeId) || { name: '' }
          ).name,
          [i18n.t('InventoryCheck:containerName')]: c.name,
          [i18n.t('InventoryCheck:lastSeenAtStoreBeforeExpiration')]:
            renderDateString(c.lastSeenAtStoreBeforeExpiration),
          [i18n.t('InventoryCheck:transactionType')]: c.transactionType,
          [i18n.t('InventoryCheck:part')]: i18n.t('InventoryCheck:Parts:0'),
          [i18n.t('InventoryCheck:status')]: i18n.t(
            `InventoryCheck:Status:${inventoryCheck.status}`,
          ),
          [i18n.t('InventoryCheck:amount')]: '-',
          [i18n.t('InventoryCheck:fromConsumer')]: yesNo(
            (consumerMapsByInventoryId[inventoryCheck.id] || {})[c.id],
          ),
          [i18n.t('InventoryCheck:billable')]: yesNo(c.isBillable),
          [i18n.t('InventoryCheck:nonBillableReason')]: c.isBillable
            ? '-'
            : i18n.t(
                `InventoryCheck:NonBillableReason:${c.nonBillableReason ?? 'None'}`,
              ),
        };
        if (showPartnerData) {
          obj[i18n.t('InventoryCheck:containerId')] = c.id;
          obj[i18n.t('InventoryCheck:seenNext')] = renderDateString(c.seenNext);
        }
        return obj;
      }),
    );

    rows = rows.concat(
      buildRowsForContainers(
        viewer,
        inventoryCheck,
        containerTypes,
        inventoryCheck.confirmedContainers,
        i18n.t('InventoryCheck:confirmed'),
      ),
    );
    rows = rows.concat(
      buildRowsForContainers(
        viewer,
        inventoryCheck,
        containerTypes,
        inventoryCheck.recoveredContainers,
        i18n.t('InventoryCheck:confirmed'),
      ),
    );
    rows = rows.concat(
      buildRowsForContainers(
        viewer,
        inventoryCheck,
        containerTypes,
        inventoryCheck.newContainers,
        i18n.t('InventoryCheck:new'),
      ),
    );
    rows = rows.concat(
      buildRowsForContainers(
        viewer,
        inventoryCheck,
        containerTypes,
        inventoryCheck.brokenContainers,
        i18n.t('InventoryCheck:broken'),
      ),
    );
  }

  return [...rows].sort(
    (a, b) =>
      a[i18n.t('InventoryCheck:typeId')] - b[i18n.t('InventoryCheck:typeId')],
  );
}

export function exportJsonAsCsv(json: any[], filename: string) {
  if (json.length === 0) return;
  const blob = toCsvBlob(json);
  download(blob, filename);
}

export function exportJsonAsExcel(json: any[], filenameBase: string) {
  if (json.length === 0) return;
  downloadJsonDataAsExcelFile(json, filenameBase);
}
